import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Mail, RefreshCw } from 'lucide-react';
import axiosInstance from '../../../lib/axios';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60; // seconds

const EmailVerification = ({ studentData, registrationState, setRegistrationState, onNext, onBack }) => {
  const registrationId = registrationState?.registrationId;
  const email = registrationState?.email || studentData?.email;
  const role = studentData?.role || 'student';

  const [codeSent, setCodeSent] = useState(!!registrationState);
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [resending, setResending] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    if (codeSent) {
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 50);
    }
  }, [codeSent]);

  const handleChange = (index, value) => {
    const cleaned = value.replace(/\D/g, '').slice(-1);
    const newOtp = [...otp];
    newOtp[index] = cleaned;
    setOtp(newOtp);
    setError('');

    if (cleaned && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!text) return;
    const newOtp = Array(OTP_LENGTH).fill('');
    text.split('').forEach((char, i) => { newOtp[i] = char; });
    setOtp(newOtp);
    inputRefs.current[Math.min(text.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleSendCode = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      if (!registrationState && studentData) {
        // studentData now contains the full payload including role and vet specific fields
        const payload = {
          ...studentData,
          phone: studentData.phone || undefined,
        };

        const res = await axiosInstance.post('/registration/initiate', payload);

        setRegistrationState({
          registrationId: res.data?.data?.registrationId,
          email: res.data?.data?.email || studentData.email,
        });
      } else {
        const regId = registrationState?.registrationId || registrationId;
        await axiosInstance.post('/registration/resend-otp', { registrationId: regId });
      }
      setSuccess('Verification code sent successfully!');
      setCodeSent(true);
      setCountdown(RESEND_COOLDOWN);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length < OTP_LENGTH) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await axiosInstance.post('/registration/verify-email', {
        registrationId,
        otp: code,
      });
      setSuccess('Email verified successfully!');
      setTimeout(() => onNext(), 800);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0 || resending) return;
    setResending(true);
    setError('');
    setSuccess('');
    try {
      await axiosInstance.post('/registration/resend-otp', { registrationId });
      setSuccess('A new verification code was sent!');
      setOtp(Array(OTP_LENGTH).fill(''));
      setCountdown(RESEND_COOLDOWN);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend code. Try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={codeSent ? () => setCodeSent(false) : onBack}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-5 transition-colors"
      >
        <ArrowLeft size={16} />
        Back
      </button>

      <h2 className="text-2xl font-bold text-gray-900 mb-1 text-center">Verify Your Email</h2>

      {/* Why verify your email alert box */}
      <div className="mt-4 mb-6 p-4 bg-blue-50/70 border border-blue-100 rounded-xl flex items-start gap-3 text-left">
        <Mail className="text-blue-500 mt-0.5 flex-shrink-0" size={18} />
        <div>
          <h4 className="text-sm font-semibold text-blue-900 mb-0.5">Why verify your email?</h4>
          <p className="text-xs text-blue-700 leading-relaxed">
            Verified {role === 'veterinarian' ? 'veterinarians' : 'students'} get full access to all features and are marked as verified members of the community.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg text-center font-medium">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg text-center font-medium">
          {success}
        </div>
      )}

      {!codeSent ? (
        <div className="text-center">
          <div className="py-6">
            <p className="text-gray-500 text-sm">We'll send a verification code to:</p>
            <p className="text-gray-900 font-semibold text-base mt-1.5 break-all">{email}</p>
          </div>

          <button
            type="button"
            onClick={handleSendCode}
            disabled={loading}
            className="w-full py-3 px-6 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Sending...</span>
              </div>
            ) : 'Send Verification Code'}
          </button>
        </div>
      ) : (
        <div>
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                Enter Verification Code
              </label>
              <div className="flex gap-2 justify-center">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={i === 0 ? handlePaste : undefined}
                    disabled={loading || !!success}
                    className={`
                      w-11 h-13 text-center text-xl font-bold border-2 rounded-xl transition-all
                      focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200
                      ${digit ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-gray-200 bg-gray-50 text-gray-900'}
                      ${loading || success ? 'opacity-60' : ''}
                    `}
                    style={{ width: '2.75rem', height: '3.25rem' }}
                  />
                ))}
              </div>
              <p className="mt-2.5 text-xs text-gray-400 text-center">
                Check your email for the 6-digit code
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || !!success}
              className="w-full py-3 px-6 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Verifying...</span>
                </div>
              ) : 'Verify Email'}
            </button>
          </form>

          <div className="mt-5 text-center">
            <button
              type="button"
              onClick={handleResend}
              disabled={countdown > 0 || resending}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RefreshCw size={14} className={resending ? 'animate-spin' : ''} />
              {countdown > 0 ? `Resend code in ${countdown}s` : resending ? 'Sending...' : 'Resend code'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailVerification;
